import { Injectable } from '@angular/core';

/**
 * Registry + click handler backing the "Copy" button on rendered code
 * blocks (see MarkdownPipe). The markdown pipe renders a
 * `<button data-copy-id="cb-N">` for every fenced code block; this service
 * stores the raw, unescaped source text for each id and attaches a single
 * document-level click listener (guarded by `listenerAttached`) that copies
 * the right snippet to the clipboard when its button is clicked.
 *
 * `providedIn: 'root'` makes this a true app-wide singleton, so the
 * document listener is attached exactly once no matter how many times the
 * markdown pipe runs or how many components inject this service.
 */
@Injectable({ providedIn: 'root' })
export class CodeCopyService {
  private registry = new Map<string, string>();
  private nextId = 0;
  private listenerAttached = false;

  register(code: string): string {
    const id = `cb-${this.nextId++}`;
    this.registry.set(id, code);
    this.ensureListener();
    return id;
  }

  private ensureListener(): void {
    if (this.listenerAttached) return;
    this.listenerAttached = true;
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const btn = target?.closest('[data-copy-id]') as HTMLElement | null;
      if (!btn) return;
      const id = btn.getAttribute('data-copy-id');
      if (!id) return;
      const code = this.registry.get(id);
      if (code == null) return;
      navigator.clipboard.writeText(code).then(() => {
        const label = btn.querySelector('.code-block__copy-label');
        if (!label) return;
        const original = label.textContent;
        label.textContent = 'Copied!';
        btn.classList.add('code-block__copy--done');
        setTimeout(() => {
          label.textContent = original;
          btn.classList.remove('code-block__copy--done');
        }, 1500);
      }).catch(() => { /* clipboard write failed silently, no crash */ });
    });
  }
}
